/** IAA 日明细与趋势：沿用数据中台容器、日期、表格与折线组件，业务与原币收入分别就绪。 */
import { type RewardedAdMetricsRow, currencyMinorUnitExponent } from '@novel-admin/shared';
import { useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import { useApps, useRewardedAdMetrics } from '~/data/queries';
import { fmtCurrencyMinor } from '~/lib/format';
import { DateRangeButton } from '~/ui/DatePicker';
import { PageTabs } from '~/ui/PageTabs';
import { Select } from '~/ui/Select';
import { TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { LineChartX } from '~/ui/charts';
import { daysBetween, toDay } from './conversion-dashboard-shared';

const PLACEMENTS = ['TASK_CENTER', 'CHAPTER_UNLOCK'] as const;
const PLACEMENT_LABELS = { TASK_CENTER: '任务中心', CHAPTER_UNLOCK: '章节解锁' };
const BUSINESS_METRICS = [
  { value: 'valid_ssv_count', label: '有效 SSV 数' },
  { value: 'bonus_granted_amount', label: '实际发放 BONUS（全额）' },
  { value: 'ad_unlock_count', label: '新增 AD 章节权益数' },
] as const;
type BusinessMetric = (typeof BUSINESS_METRICS)[number]['value'];

/** 日期选择器按本地日历展示 UTC 日串，避免浏览器时区把默认截止日移到今天。 */
function calendarDate(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, date ?? 1);
}
function initialRange(): [Date, Date] {
  const end = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
  const start = new Date(Date.parse(`${end}T00:00:00Z`) - 29 * 86_400_000)
    .toISOString()
    .slice(0, 10);
  return [calendarDate(start), calendarDate(end)];
}
function count(value: string): string {
  return BigInt(value).toLocaleString('en-US');
}
/** 轴刻度仅为图形定位，使用整数短标签；完整整数和原币金额保留在明细。 */
function chartAxis(value: number): string {
  return Math.round(value).toLocaleString('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  });
}
/** 图表只接受能精确承载的整数，未就绪与超范围数值均断点；明细始终保留完整值。 */
function chartInteger(value: string | undefined): number | null {
  if (value === undefined) return null;
  const integer = BigInt(value);
  return integer > BigInt(Number.MAX_SAFE_INTEGER) || integer < BigInt(Number.MIN_SAFE_INTEGER)
    ? null
    : Number(integer);
}

function DailyRows({ rows }: { rows: RewardedAdMetricsRow[] }) {
  return (
    <div className="card" style={{ overflowX: 'auto' }}>
      <Table className="tbl" aria-label="IAA 日统计明细">
        <thead>
          <tr>
            <th>日期</th>
            <th>用途</th>
            <th>业务状态 · UTC</th>
            <th>有效 SSV</th>
            <th>BONUS 全额</th>
            <th>新增 AD 权益</th>
            <th>原币预估收入 · 平台报表日</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.date}:${row.placement}`}>
              <td className="mono">{row.date}</td>
              <td>{PLACEMENT_LABELS[row.placement]}</td>
              <td>
                <span
                  className={`chip ${row.business.status === 'READY' ? 'chip-pos' : 'chip-warn'}`}
                >
                  {row.business.status === 'READY' ? '业务已就绪' : '业务未就绪'}
                </span>
              </td>
              <td className="mono">
                {row.business.status === 'READY' ? count(row.business.valid_ssv_count) : '—'}
              </td>
              <td className="mono">
                {row.business.status === 'READY' ? count(row.business.bonus_granted_amount) : '—'}
              </td>
              <td className="mono">
                {row.business.status === 'READY' ? count(row.business.ad_unlock_count) : '—'}
              </td>
              <td>
                {row.revenue.status === 'PENDING_INSTRUMENTATION' ? (
                  <span className="chip chip-warn">收入未就绪</span>
                ) : (
                  <>
                    <span className="chip chip-pos">收入已就绪 · 预估</span>
                    {row.revenue.estimates.map((estimate) => (
                      <div key={`${estimate.currency}:${estimate.report_timezone}`}>
                        <strong className="mono">
                          {estimate.currency}{' '}
                          {currencyMinorUnitExponent(estimate.currency) === null
                            ? `${estimate.amount_minor_units} 最小货币单位（精度未识别）`
                            : fmtCurrencyMinor(
                                estimate.amount_minor_units,
                                estimate.currency,
                                currencyMinorUnitExponent(estimate.currency) ?? 0,
                              )}
                        </strong>
                        <div className="muted">报表时区：{estimate.report_timezone}</div>
                        <div className="muted mono">成功更新时间：{estimate.updated_at}</div>
                      </div>
                    ))}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function Trends({ rows, days }: { rows: RewardedAdMetricsRow[]; days: string[] }) {
  const [metric, setMetric] = useState<BusinessMetric>('valid_ssv_count');
  const metricLabel = BUSINESS_METRICS.find((item) => item.value === metric)?.label ?? '';
  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div className="hstack" style={{ gap: 8 }}>
        <span>业务趋势指标</span>
        <Select
          value={metric}
          name="businessMetric"
          onChange={(event) => setMetric(event.target.value as BusinessMetric)}
        >
          {BUSINESS_METRICS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>
      </div>
      <p className="muted">未就绪或超出图表安全整数范围的日为断点，精确数值见日明细。</p>
      {PLACEMENTS.map((placement) => {
        const daily = new Map(
          rows.filter((row) => row.placement === placement).map((row) => [row.date, row]),
        );
        const revenueGroups = new Map<string, { currency: string; timezone: string }>();
        for (const row of daily.values()) {
          if (row.revenue.status !== 'READY') continue;
          for (const estimate of row.revenue.estimates) {
            revenueGroups.set(JSON.stringify([estimate.currency, estimate.report_timezone]), {
              currency: estimate.currency,
              timezone: estimate.report_timezone,
            });
          }
        }
        return (
          <section key={placement} className="card" style={{ padding: 16 }}>
            <h3>
              {PLACEMENT_LABELS[placement]} · {metricLabel} · UTC 业务日
            </h3>
            <LineChartX
              yFormat={chartAxis}
              xLabels={days}
              series={[
                {
                  label: metricLabel,
                  color: 'var(--chart-1)',
                  values: days.map((day) => {
                    const business = daily.get(day)?.business;
                    return chartInteger(
                      business?.status === 'READY' ? business[metric] : undefined,
                    );
                  }),
                },
              ]}
            />
            <h3>原币预估收入 · 平台报表日</h3>
            {revenueGroups.size === 0 && <p className="muted">收入未就绪，暂无完整报表趋势。</p>}
            {[...revenueGroups].map(([key, group]) => (
              <div key={key}>
                <p>
                  {group.currency} · {group.timezone} · 最小货币单位 · 预估
                </p>
                <LineChartX
                  allowNegative
                  yFormat={chartAxis}
                  xLabels={days}
                  series={[
                    {
                      label: `${group.currency} 最小货币单位`,
                      color: 'var(--chart-2)',
                      values: days.map((day) => {
                        const revenue = daily.get(day)?.revenue;
                        const estimate =
                          revenue?.status === 'READY'
                            ? revenue.estimates.find(
                                (item) =>
                                  item.currency === group.currency &&
                                  item.report_timezone === group.timezone,
                              )
                            : undefined;
                        return chartInteger(estimate?.amount_minor_units);
                      }),
                    },
                  ]}
                />
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}

export default function MetricsRewardedAdsPage() {
  const appScope = useAppScope((state) => state.appId);
  const apps = useApps();
  const [range, setRange] = useState<[Date, Date]>(initialRange);
  const [pickerRevision, setPickerRevision] = useState(0);
  const [view, setView] = useState<'daily' | 'trend'>('daily');
  const from = toDay(range[0]);
  const to = toDay(range[1]);
  const query = useRewardedAdMetrics({ appScope, from, to });
  const groups = new Map<string, RewardedAdMetricsRow[]>();
  for (const row of query.data?.rows ?? []) {
    const group = groups.get(row.app_id) ?? [];
    group.push(row);
    groups.set(row.app_id, group);
  }
  const changeRange = (value: [Date, Date]) => {
    const first = toDay(value[0]);
    const last = toDay(value[1]);
    const dayCount =
      (Date.parse(`${last}T00:00:00Z`) - Date.parse(`${first}T00:00:00Z`)) / 86_400_000 + 1;
    const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
    if (!Number.isFinite(dayCount) || dayCount < 1 || dayCount > 90 || last > yesterday) {
      toast('请选择合法闭区间，最多 90 日，截止日不能晚于昨天 UTC。', { tone: 'warn' });
      // 现有选择器维护内部草稿；拒绝后重置到实际查询窗，避免显示未查询的非法区间。
      setPickerRevision((revision) => revision + 1);
      return;
    }
    setRange(value);
  };
  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">IAA 激励广告统计</h1>
          <p className="page-sub">业务事实与 AdMob 原币预估收入 · 按 App 和用途分别查看</p>
        </div>
        <DateRangeButton
          key={`${from}:${to}:${pickerRevision}`}
          value={range}
          label={`${from} — ${to} · UTC`}
          referenceDate={initialRange()[1]}
          onChange={changeRange}
        />
      </div>
      <p className="muted">App 跟随顶栏选择；all 仅含启用的 iOS / Android App，WEB 不支持。</p>
      <p className="muted">
        业务日按 UTC；收入日期保留平台报表日与时区，相同日期不代表同一绝对时间窗。BONUS
        是积分全额，不是货币收入。
      </p>
      <p className="muted">
        收入均为预估，最终结算以平台结算为准；未完整覆盖时不展示局部金额或全 App 总额。
      </p>
      <PageTabs
        label="IAA 统计视图"
        value={view}
        onChange={setView}
        items={[
          { id: 'daily', label: '日明细' },
          { id: 'trend', label: '时间趋势' },
        ]}
      />
      {apps.isError && (
        <p role="status" className="muted">
          App 名称和平台读取失败，保留 App ID。
          <button type="button" className="btn btn-sm" onClick={apps.refetch}>
            重试 App 信息
          </button>
        </p>
      )}
      {query.isLoading ? (
        <TableSkeleton />
      ) : query.isError ? (
        <div role="alert" className="card" style={{ padding: 16 }}>
          <p>IAA 查询失败：{query.error.message}</p>
          <button type="button" className="btn" onClick={() => void query.refetch()}>
            重新加载
          </button>
        </div>
      ) : groups.size === 0 ? (
        <div className="card" style={{ padding: 16 }}>
          当前范围没有原生 App 统计行。
        </div>
      ) : (
        [...groups].map(([appId, rows]) => {
          const app = apps.data.find((item) => item.id === appId);
          return (
            <section key={appId} className="vstack" style={{ gap: 12, marginTop: 20 }}>
              <h2>
                {app?.name ?? appId}{' '}
                <span className="chip">
                  {app?.platform === 'iOS'
                    ? 'iOS'
                    : app?.platform === 'Android'
                      ? 'Android'
                      : '平台信息未加载'}
                </span>
              </h2>
              <div className="muted mono">{appId}</div>
              {view === 'daily' ? (
                <DailyRows rows={rows} />
              ) : (
                <Trends rows={rows} days={daysBetween(from, to)} />
              )}
            </section>
          );
        })
      )}
    </div>
  );
}
